import React, { useState } from "react";
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "../common/stripeClient";
import { discoveryApi } from "./discoveryApi";
import { apiMessage } from "./fanHomeApi";
import { Dialog, INK, MUTED, OutlineButton, SolidButton } from "./libraryParts";

// My Licences > Update card. Collects a new card with Stripe Elements (the card number never reaches our server), saves it on the buyer's
// running licences and lets the server try a failed renewal again straight away. The result is read from Stripe by the server.
const ELEMENT_OPTIONS = {
  style: { base: { fontSize: "16px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" }, invalid: { color: "#BE1F2E" } },
};

function Fields({ onSaved, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!stripe || !elements) { setError("The card form is still loading. Please try again in a moment."); return; }
    if (!name.trim()) { setError("Enter the name on the card."); return; }
    setBusy(true);
    setError("");
    try {
      const started = await discoveryApi.renewalCardStart();
      const { error: cardError } = await stripe.confirmCardSetup(started.client_secret, {
        payment_method: { card: elements.getElement(CardNumberElement), billing_details: { name: name.trim() } },
      });
      if (cardError) { setError(`${cardError.message} Your card was not changed.`); return; }
      const done = await discoveryApi.renewalCardConfirm(started.setup_intent_id);
      onSaved(done);
    } catch (e) {
      setError(apiMessage(e, "We could not save the card. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="renewal-card-form">
      <p className="text-sm leading-relaxed" style={{ color: MUTED }}>
        The new card is used for automatic renewal of your licences. If a renewal failed, it is tried again as soon as the card is saved.
      </p>
      <label className="mt-4 block text-sm text-gray-600" htmlFor="renewal-card-name">Name on card</label>
      <input id="renewal-card-name" type="text" autoComplete="cc-name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-3 text-base" />
      <label className="mt-3 block text-sm text-gray-600">Card number</label>
      <div className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-3"><CardNumberElement options={ELEMENT_OPTIONS} /></div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-600">Expiry date</label>
          <div className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-3"><CardExpiryElement options={ELEMENT_OPTIONS} /></div>
        </div>
        <div>
          <label className="block text-sm text-gray-600">CVC</label>
          <div className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-3"><CardCvcElement options={ELEMENT_OPTIONS} /></div>
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600" role="alert" data-testid="renewal-card-error">{error}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <OutlineButton onClick={onClose} disabled={busy}>Cancel</OutlineButton>
        <SolidButton onClick={save} disabled={busy || !stripe}>{busy ? "Saving…" : "Save card"}</SolidButton>
      </div>
      <p className="mt-3 text-xs" style={{ color: MUTED }}>Your card is processed securely by Stripe.</p>
    </div>
  );
}

export default function RenewalCardDialog({ onSaved, onClose }) {
  return (
    <Dialog title="Update your renewal card" onClose={onClose}>
      <span className="sr-only" style={{ color: INK }}>Card details</span>
      <Elements stripe={getStripe()}>
        <Fields onSaved={onSaved} onClose={onClose} />
      </Elements>
    </Dialog>
  );
}
